import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { isMockMode } from '../../services/repository'
import { ApiError } from '../../api/client'

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError || err instanceof Error) return err.message
  return fallback
}

export function LoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { login, isAuthenticated, isLoading } = useAuthStore()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  if (!isLoading && isAuthenticated) {
    return <Navigate to="/" replace />
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError('')
    setSubmitting(true)
    try {
      await login(email, password)
      navigate('/', { replace: true })
    } catch (err) {
      setError(errorMessage(err, t('auth.errors.loginFailed', '登录失败')))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <Link
          to="/"
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-6"
        >
          ← {t('auth.backHome', '返回游戏')}
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 mb-1">{t('auth.login', '登录')}</h1>
        <p className="text-sm text-gray-500 mb-6">
          {t('auth.loginTagline', '登录后可云端同步进度与订阅 Pro')}
        </p>

        <form
          onSubmit={onSubmit}
          className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('auth.email', '邮箱')}
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              placeholder="you@example.com"
              autoComplete="email"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('auth.password', '密码')}
            </label>
            <input
              type="password"
              required
              minLength={8}
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
              autoComplete="current-password"
            />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-xl bg-violet-600 text-white py-2.5 text-sm font-medium hover:bg-violet-700 disabled:opacity-60 transition-colors"
          >
            {submitting ? t('auth.submitting', '提交中…') : t('auth.login', '登录')}
          </button>
        </form>

        <p className="text-sm text-gray-500 mt-4 text-center">
          {t('auth.noAccount', '还没有账号？')}{' '}
          <Link to="/register" className="text-violet-600 hover:underline">
            {t('auth.register', '注册')}
          </Link>
        </p>

        {isMockMode && (
          <p className="text-xs text-gray-400 mt-3 text-center">
            Mock: demo@numgame.local / demo1234
          </p>
        )}
      </div>
    </div>
  )
}
